import React, { useState, useEffect } from 'react';
import {
    Typography,
    Box
} from '@mui/material';
import { CreditCard } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { ConfirmationModal } from '../../../../../components/ConfirmationModal';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';
import { useCreatePhysicalCardMutation, useFetchDataQuery } from '../../../services/services';
import CreatePhysicalCardForm, { PhysicalCardFormData, PhysicalCardRequestData } from './CreatePhysicalCardForm';

// Card service interfaces
interface CreatePhysicalCardResponse {
    success: boolean;
    message: string;
}

interface CreatePhysicalCardActionProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const CreatePhysicalCardAction: React.FC<CreatePhysicalCardActionProps> = ({ isOpen, onClose, onSuccess }) => {
    const { currentUserState } = useSelector((state: RootState) => state.currentUserState);
    const [loading, setLoading] = useState(false);
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
    const [formData, setFormData] = useState<PhysicalCardFormData>({
        userName: '',
        userSurName: '',
        email: '',
        cityName: '',
        countyName: '',
        neighborhood: '',
        street: '',
        phoneNumber: '',
        cardType: '',
        buildingNo: '',
        cityId: 0,
        countyId: 0,
        neighborhoodId: 0,
        companyId: currentUserState?.companyId || 0,
    });
    const [requestData, setRequestData] = useState<PhysicalCardRequestData | null>(null);
    const [isFormValid, setIsFormValid] = useState(false);

    const [createPhysicalCard] = useCreatePhysicalCardMutation();

    useEffect(() => {
        if (!isOpen) {
            setShowSubmitConfirm(false);
        }
    }, [isOpen]);

    const handleFormChange = (newFormData: PhysicalCardFormData) => {
        setFormData(newFormData);
    };

    const handleValidationChange = (isValid: boolean) => {
        setIsFormValid(isValid);
    };

    const handleRequestDataChange = (newRequestData: PhysicalCardRequestData | null) => {
        setRequestData(newRequestData);
    };

    const handleRequestSubmit = () => {
        if (!isFormValid || !requestData) {
            enqueueSnackbar('Lütfen tüm alanları doldurun.', {
                variant: 'error'
            });
            return;
        }
        setShowSubmitConfirm(true);
    };

    const performSubmit = async () => {
        if (!isFormValid || !requestData) {
            return;
        }

        try {
            setLoading(true);
            const result = await createPhysicalCard(requestData);
            const response: CreatePhysicalCardResponse | undefined = (result as any).data;

            if (response?.success) {
                enqueueSnackbar(response.message, {
                    variant: 'success'
                });

                onSuccess?.();
                handleClose();
            } else {
                enqueueSnackbar(response?.message || 'Fiziki kart oluşturulurken bir hata oluştu', {
                    variant: 'error'
                });
            }
        } catch (error: any) {
            const errorMessage = error?.error?.message || error?.message || 'Bilinmeyen bir hata oluştu';
            enqueueSnackbar(errorMessage, {
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setShowSubmitConfirm(false);
        setFormData({
            userName: '',
            userSurName: '',
            email: '',
            cityName: '',
            countyName: '',
            phoneNumber: '',
            cardType: '',
            neighborhood: '',
            street: '',
            buildingNo: '',
            cityId: 0,
            countyId: 0,
            neighborhoodId: 0,
            companyId: currentUserState?.companyId || 0,
        });
        setRequestData(null);
        setIsFormValid(false);
        onClose();
    };

    const submitConfirmContent =
        formData.userName.trim() || formData.userSurName.trim()
            ? `${formData.userName.trim()} ${formData.userSurName.trim()}`.trim() +
              ' için fiziki kart oluşturmak istediğinize emin misiniz?'
            : 'Fiziki kart oluşturmak istediğinize emin misiniz?';

    return (
        <>
            <ConfirmationModal
                isOpen={isOpen}
                onClose={handleClose}
                onConfirm={handleRequestSubmit}
                title="Fiziki Kart Oluştur"
                isLoading={false}
                confirmButtonText="Oluştur"
                closeButtonText="Vazgeç"
                optionalWidth="800px"
                customHeaderStyle={{
                    icon: (
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                bgcolor: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CreditCard sx={{ fontSize: 18 }} />
                        </Box>
                    ),
                }}
            >
                <Box sx={{ py: 1 }}>
                    <Typography variant="body2" sx={{
                        color: '#64748b',
                        mb: 2,
                        textAlign: 'center'
                    }}>
                        Fiziki kartınız için gerekli bilgileri doldurun. Kart belirlediğiniz adrese kargo ile gönderilecektir.
                    </Typography>

                    <CreatePhysicalCardForm
                        onFormChange={handleFormChange}
                        onValidationChange={handleValidationChange}
                        onRequestDataChange={handleRequestDataChange}
                    />
                </Box>
            </ConfirmationModal>

            <ConfirmationModal
                isOpen={showSubmitConfirm}
                onClose={() => setShowSubmitConfirm(false)}
                onConfirm={() => void performSubmit()}
                title="İşlem Onayı"
                content={submitConfirmContent}
                confirmButtonText={loading ? 'Oluşturuluyor...' : 'Onayla'}
                closeButtonText="İptal"
                isLoading={loading}
                optionalWidth="440px"
            />
        </>
    );
};

export default CreatePhysicalCardAction;
